import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'react-bootstrap-icons';
import PageLayout from '../../layouts/PageLayout';
import DateRangePicker, { calcDatePreset } from '../../components/fields/DateRangePicker';
import { useBranch } from '../../context/BranchContext';
import { bankAccountAPI, configurationAPI } from '../../services';
import { toast } from 'react-toastify';

const CONFIG_KEY_PAYMENT_QR_ACCOUNT = 'PAYMENT_QR_ACCOUNT_CONFIG';

const defaultRevenueRange = calcDatePreset('last7days');

const formatVnd = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(n);
};

const normalizeAccount = (account) => {
  if (!account) return null;
  const hasReceived = account.received_amount != null || account.received_order_count != null;
  return {
    id: account.id,
    short_name: account.short_name ?? account.shortName ?? '',
    bank_name: account.bank_name,
    account_no: account.account_no,
    account_name: account.account_name,
    acq_id: account.acq_id,
    vietqr_client_id: account.vietqr_client_id || '',
    vietqr_api_key: account.vietqr_api_key || '',
    static_qr_url: account.static_qr_url || '',
    notes: account.notes || '',
    is_active: account.is_active ?? account.isActive ?? true,
    display_order:
      account.display_order ?? account.displayOrder ?? account.sort_order ?? account.order ?? account.position ?? 0,
    created_at: account.created_at || account.createdAt,
    updated_at: account.updated_at || account.updatedAt,
    received_amount: hasReceived ? Number(account.received_amount ?? 0) : null,
    received_order_count: hasReceived ? Number(account.received_order_count ?? 0) : null,
  };
};

const normalizeAssignment = (assignment) => {
  if (!assignment) return null;
  return {
    id: assignment.id,
    branch_id: assignment.branch_id,
    bank_account_id: assignment.bank_account_id,
    is_default: assignment.is_default ?? assignment.isDefault ?? false,
    is_active: assignment.is_active ?? assignment.isActive ?? true,
    display_order:
      assignment.display_order ??
      assignment.displayOrder ??
      assignment.sort_order ??
      assignment.order ??
      assignment.position ??
      0,
    bank_account: normalizeAccount(assignment.bank_account),
  };
};

const emptyAccountForm = {
  bank_name: '',
  short_name: '',
  account_no: '',
  account_name: '',
  acq_id: '',
  vietqr_client_id: '',
  vietqr_api_key: '',
  static_qr_url: '',
  notes: '',
  display_order: '',
  is_active: true,
};

const emptyAssignForm = {
  bank_account_id: '',
  is_active: true,
  display_order: '',
};

const formatBankOption = (bank) => `${bank.shortName || bank.code || bank.name} - ${bank.name} (${bank.bin})`;

const findBankByInput = (banks, rawValue) => {
  if (!rawValue) return null;
  const value = rawValue.trim().toLowerCase();
  if (!value) return null;
  return (
    banks.find((bank) => formatBankOption(bank).toLowerCase() === value) ||
    banks.find((bank) => bank.bin?.toLowerCase() === value) ||
    banks.find((bank) => bank.code?.toLowerCase() === value) ||
    banks.find((bank) => bank.shortName?.toLowerCase() === value) ||
    banks.find((bank) => bank.name?.toLowerCase() === value)
  );
};

// (#002727) Một tài khoản của chi nhánh, hiển thị dạng THẺ chứ không phải dòng bảng.
// Lý do: bảng 5 cột rộng 466px trong khung 324px của điện thoại 390px — cột "Thứ tự" nằm ngoài
// mép phải, phải vuốt ngang mới thấy, nên trên máy sếp trông như "không hiện số thứ tự".
// Thẻ xếp dọc thì số vị trí và ô Thứ tự luôn nằm trong màn, không phải vuốt.
// Chỉ tay cầm (biểu tượng ⠿) mới bắt sự kiện kéo: nếu gắn listeners lên cả thẻ thì trên điện
// thoại không vuốt cuộn trang được nữa, và bấm vào ô "Thứ tự" cũng bị hiểu là bắt đầu kéo.
const SortableAssignmentCard = ({
  assignment,
  index,
  total,
  savingOrderId,
  orderDrafts,
  setOrderDrafts,
  onSaveOrder,
  onMove,
  onUpdate,
  onRemove,
  positionNote,
  revenuePosition,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: assignment.id,
  });
  const bankName = assignment.bank_account?.bank_name || 'tài khoản';
  const saving = savingOrderId === assignment.id;
  const isFirst = index === 0;

  return (
    <div
      ref={setNodeRef}
      className={`border rounded p-2 mb-2 ${isFirst ? 'border-success border-2' : ''}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        background: isDragging ? '#eef4ff' : '#fff',
      }}
    >
      <div className="d-flex align-items-center gap-2">
        <span
          {...attributes}
          {...listeners}
          role="button"
          aria-label={`Kéo để đổi vị trí của ${bankName}`}
          title="Kéo để đổi vị trí"
          className="text-muted flex-shrink-0"
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', padding: '6px 2px' }}
        >
          <GripVertical size={20} />
        </span>
        <span
          className={`badge flex-shrink-0 ${isFirst ? 'bg-success' : 'bg-secondary'}`}
          style={{ fontSize: 15, minWidth: 30 }}
        >
          {index + 1}
        </span>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">
            {assignment.bank_account?.short_name || assignment.bank_account?.bank_name}
          </div>
          <small className="text-muted d-block text-truncate">
            {assignment.bank_account?.account_no} - {assignment.bank_account?.account_name}
          </small>
        </div>
        <span
          className={`badge flex-shrink-0 ${assignment.is_active ? 'bg-success-subtle text-success' : 'bg-secondary'}`}
        >
          {assignment.is_active ? 'Bật' : 'Tắt'}
        </span>
      </div>

      <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
        <span className="small text-muted">Thứ tự</span>
        <input
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          aria-label={`Thứ tự của ${bankName}`}
          className="form-control form-control-sm text-center px-1"
          style={{ width: 64 }}
          disabled={saving}
          value={orderDrafts[assignment.id] ?? String(assignment.display_order ?? '')}
          onChange={(e) => setOrderDrafts((prev) => ({ ...prev, [assignment.id]: e.target.value }))}
          onBlur={() => onSaveOrder(assignment)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          title="Lên trên"
          disabled={index === 0 || saving}
          onClick={() => onMove(assignment, -1)}
        >
          ▲
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          title="Xuống dưới"
          disabled={index === total - 1 || saving}
          onClick={() => onMove(assignment, 1)}
        >
          ▼
        </button>
        <div className="ms-auto d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-warning"
            onClick={() => onUpdate(assignment.id, { is_active: !assignment.is_active })}
          >
            {assignment.is_active ? 'Tắt' : 'Bật'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(assignment)}>
            Gỡ
          </button>
        </div>
      </div>

      {/* (#002727) Trước đây chỉ thẻ đầu có dòng "nhận TOÀN BỘ" — sai ngay khi bật chia theo mức.
          Nay MỖI thẻ nói rõ nó nhận đơn ở mức nào, chữ lấy từ máy chủ. */}
      {positionNote && (
        <div className={`small mt-1 ${isFirst ? 'text-success' : 'text-muted'}`}>{positionNote}</div>
      )}

      {/* (#214922) Mốc tiền ở trên chỉ đúng khi vị trí này còn dư hạn mức doanh thu. Vượt rồi thì
          MỌI đơn lẽ ra vào đây — kể cả đơn nhỏ nhất — đang bị đẩy sang vị trí khác. Không nói ra
          thì nhìn màn hình không thể biết (chính là chuyện sếp gặp ở #212048). */}
      {revenuePosition?.exceeded && revenuePosition.actual_position !== revenuePosition.position && (
        <div className="small mt-1 text-danger fw-semibold">
          ⚠️ Đã vượt hạn mức doanh thu — mốc tiền ở trên KHÔNG còn hiệu lực với vị trí này, đơn đang
          chảy sang vị trí {revenuePosition.actual_position}.
          {revenuePosition.week_limit > 0 && (
            <> Tuần: {Number(revenuePosition.week_revenue || 0).toLocaleString('vi-VN')}đ/
              {Number(revenuePosition.week_limit).toLocaleString('vi-VN')}đ.</>
          )}
          {revenuePosition.month_limit > 0 && (
            <> Tháng: {Number(revenuePosition.month_revenue || 0).toLocaleString('vi-VN')}đ/
              {Number(revenuePosition.month_limit).toLocaleString('vi-VN')}đ.</>
          )}
        </div>
      )}
    </div>
  );
};

const BankAccountManagementPage = () => {
  const { selectedBranch } = useBranch();
  const branchId = selectedBranch?.id;

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountForm, setAccountForm] = useState(() => ({ ...emptyAccountForm }));
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);
  // (#195715) đang tải ảnh mã QR tĩnh lên kho ảnh — khoá nút lưu trong lúc chờ.
  const [uploadingStaticQr, setUploadingStaticQr] = useState(false);

  const [branchAssignments, setBranchAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  // (#002727) Ô "Thứ tự" phải là controlled input: giá trị đang gõ giữ ở đây, khoá theo id
  // của dòng gán. Trước đây dùng defaultValue= (uncontrolled) nên sau khi lưu/tải lại danh sách
  // ô không đồng bộ theo dữ liệu mới.
  const [orderDrafts, setOrderDrafts] = useState({});
  const [savingOrderId, setSavingOrderId] = useState(null);
  const [assignForm, setAssignForm] = useState(() => ({ ...emptyAssignForm }));
  const [assigning, setAssigning] = useState(false);
  const [vietqrBanks, setVietqrBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankSearch, setBankSearch] = useState('');

  // (#002727) Quy tắc chia tiền theo mức — đọc từ máy chủ bằng chính hàm mà bộ sinh QR dùng.
  const [splitConfig, setSplitConfig] = useState(null);
  const [loadingSplitConfig, setLoadingSplitConfig] = useState(false);
  const [splitEnabledDraft, setSplitEnabledDraft] = useState(false);
  const [tierDrafts, setTierDrafts] = useState([]);
  const [savingSplitConfig, setSavingSplitConfig] = useState(false);
  const [showRawQrConfig, setShowRawQrConfig] = useState(false);

  const [paymentQrConfig, setPaymentQrConfig] = useState(null);
  const [loadingPaymentQrConfig, setLoadingPaymentQrConfig] = useState(false);
  const [paymentQrConfigEditValue, setPaymentQrConfigEditValue] = useState('');
  const [savingPaymentQrConfig, setSavingPaymentQrConfig] = useState(false);

  const [showAccountModal, setShowAccountModal] = useState(false);
  const [revenueDateFrom, setRevenueDateFrom] = useState(defaultRevenueRange.from);
  const [revenueDateTo, setRevenueDateTo] = useState(defaultRevenueRange.to);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      const response = await bankAccountAPI.getBankAccounts({
        date_from: revenueDateFrom,
        date_to: revenueDateTo,
      });
      const list = Array.isArray(response) ? response : (response?.data ?? []);
      const normalized = (list || []).map((item) => normalizeAccount(item)).filter(Boolean);
      setAccounts(normalized);
    } catch (error) {
      console.error('Cannot load bank accounts', error);
      toast.error('Không tải được danh sách tài khoản ngân hàng');
    } finally {
      setLoadingAccounts(false);
    }
  }, [revenueDateFrom, revenueDateTo]);

  const fetchAssignments = useCallback(async () => {
    if (!branchId) {
      setBranchAssignments([]);
      return;
    }
    try {
      setLoadingAssignments(true);
      const response = await bankAccountAPI.getBranchBankAccounts(branchId);
      const payload = response?.data || response;
      const normalized = (payload?.bank_accounts || payload || [])
        .map((item) => normalizeAssignment(item))
        .filter(Boolean);
      setBranchAssignments(normalized);
      // Dữ liệu mới về thì bỏ hết bản nháp đang gõ, ô luôn phản ánh đúng CSDL.
      setOrderDrafts({});
    } catch (error) {
      console.error('Cannot load branch bank accounts', error);
      toast.error('Không tải được danh sách tài khoản của chi nhánh');
    } finally {
      setLoadingAssignments(false);
    }
  }, [branchId]);

  const fetchSplitConfig = useCallback(async () => {
    if (!branchId) {
      setSplitConfig(null);
      return;
    }
    try {
      setLoadingSplitConfig(true);
      const res = await bankAccountAPI.getQrSplitConfig(branchId);
      const data = res?.data || res;
      setSplitConfig(data || null);
      setSplitEnabledDraft(!!data?.enabled);
      setTierDrafts((data?.amount_tiers || []).map((n) => String(n)));
    } catch (error) {
      console.error('Cannot load QR split config', error);
      setSplitConfig(null);
    } finally {
      setLoadingSplitConfig(false);
    }
  }, [branchId]);

  const handleAddTier = () => setTierDrafts((prev) => [...prev, '']);

  const handleRemoveTier = (idx) =>
    setTierDrafts((prev) => prev.filter((_, i) => i !== idx));

  const handleChangeTier = (idx, value) =>
    setTierDrafts((prev) => prev.map((v, i) => (i === idx ? value : v)));

  const handleSaveSplitConfig = async () => {
    if (!branchId) {
      toast.error('Chọn chi nhánh trước đã');
      return;
    }
    // Bỏ dấu chấm/phẩy người dùng gõ cho dễ đọc (300.000) trước khi gửi.
    const tiers = tierDrafts
      .map((v) => Number(String(v).replace(/[.,\s]/g, '')))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (splitEnabledDraft && !tiers.length) {
      toast.error('Bật chia tiền theo mức thì phải có ít nhất 1 mốc tiền');
      return;
    }
    if (new Set(tiers).size !== tiers.length) {
      toast.error('Các mốc tiền không được trùng nhau');
      return;
    }

    try {
      setSavingSplitConfig(true);
      const res = await bankAccountAPI.updateQrSplitConfig(branchId, {
        enabled: splitEnabledDraft,
        amount_tiers: tiers,
      });
      const data = res?.data || res;
      setSplitConfig(data || null);
      setSplitEnabledDraft(!!data?.enabled);
      setTierDrafts((data?.amount_tiers || []).map((n) => String(n)));
      toast.success(
        splitEnabledDraft
          ? 'Đã lưu quy tắc chia tiền theo mức'
          : 'Đã tắt chia theo mức — vị trí 1 nhận toàn bộ',
      );
      // Ô JSON "Nâng cao" bên dưới đọc cùng bản ghi cấu hình — nạp lại để không hiện bản cũ.
      await fetchPaymentQrConfig();
    } catch (error) {
      console.error('Cannot save QR split config', error);
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          'Không lưu được quy tắc chia tiền',
      );
    } finally {
      setSavingSplitConfig(false);
    }
  };

  const fetchPaymentQrConfig = useCallback(async () => {
    try {
      setLoadingPaymentQrConfig(true);
      const res = await configurationAPI.getConfigurations({
        search: CONFIG_KEY_PAYMENT_QR_ACCOUNT,
        branch_id: branchId || '',
      });
      const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : (res?.items ?? []);
      const raw = arr.find((c) => (c.key || c.Key) === CONFIG_KEY_PAYMENT_QR_ACCOUNT) || null;
      setPaymentQrConfig(raw || null);
      if (raw?.value != null) {
        const val = raw.value;
        setPaymentQrConfigEditValue(
          typeof val === 'string' ? val : typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val),
        );
      } else {
        setPaymentQrConfigEditValue('');
      }
    } catch (error) {
      console.error('Cannot load PAYMENT_QR_ACCOUNT_CONFIG', error);
      setPaymentQrConfig(null);
      setPaymentQrConfigEditValue('');
    } finally {
      setLoadingPaymentQrConfig(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    fetchPaymentQrConfig();
  }, [fetchPaymentQrConfig]);

  useEffect(() => {
    fetchSplitConfig();
  }, [fetchSplitConfig]);

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    (async () => {
      try {
        setLoadingBanks(true);
        const response = await fetch('https://api.vietqr.io/v2/banks', { signal: controller.signal });
        const json = await response.json();
        if (!ignore) {
          setVietqrBanks(Array.isArray(json?.data) ? json.data : []);
        }
      } catch (error) {
        if (!ignore && error.name !== 'AbortError') {
          console.error('Cannot fetch VietQR banks', error);
          toast.error('Không tải được danh sách ngân hàng VietQR');
        }
      } finally {
        if (!ignore) {
          setLoadingBanks(false);
        }
      }
    })();
    return () => {
      ignore = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    setAssignForm({ ...emptyAssignForm });
  }, [branchId]);

  const resetAccountFormFields = () => {
    setAccountForm({ ...emptyAccountForm });
    setEditingAccountId(null);
    setBankSearch('');
  };

  const closeAccountModal = () => {
    setShowAccountModal(false);
    resetAccountFormFields();
  };

  const openAddAccountModal = () => {
    resetAccountFormFields();
    setShowAccountModal(true);
  };

  // (#195715) Tải ảnh mã QR tĩnh lên KHO ẢNH DÙNG CHUNG qua máy chủ
  // (`POST /bank-accounts/static-qr`, chỉ ADMIN). Chỉ nhận ảnh PNG/JPG/WebP, tối đa 5MB.
  const handleStaticQrFileChange = async (event) => {
    const file = event.target.files?.[0];
    // Cho chọn lại đúng cùng 1 tệp ở lần sau.
    event.target.value = '';
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      toast.error('Chỉ nhận tệp ảnh (PNG, JPG, WebP) cho mã QR tĩnh');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ảnh mã QR tĩnh tối đa 5MB');
      return;
    }

    try {
      setUploadingStaticQr(true);
      const uploaded = await bankAccountAPI.uploadStaticQr(file);
      const url = uploaded?.url || uploaded?.data?.url;
      if (!url) {
        throw new Error('Máy chủ không trả về đường dẫn ảnh');
      }
      setAccountForm((prev) => ({ ...prev, static_qr_url: url }));
      toast.success('Đã tải ảnh mã QR tĩnh lên — bấm Lưu để áp dụng');
    } catch (error) {
      console.error('Cannot upload static QR', error);
      toast.error(error?.message || 'Tải ảnh mã QR tĩnh thất bại');
    } finally {
      setUploadingStaticQr(false);
    }
  };

  const handleAccountSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      bank_name: accountForm.bank_name.trim(),
      account_no: accountForm.account_no.trim(),
      account_name: accountForm.account_name.trim(),
      short_name: (accountForm.short_name || '').trim() || undefined,
      acq_id: accountForm.acq_id.trim(),
      vietqr_client_id: accountForm.vietqr_client_id.trim() || undefined,
      vietqr_api_key: accountForm.vietqr_api_key.trim() || undefined,
      // (#195715) Ảnh mã QR tĩnh: gửi chuỗi rỗng quy về null để XOÁ được ảnh đã lưu.
      static_qr_url: (accountForm.static_qr_url || '').trim() || null,
      notes: accountForm.notes.trim() || undefined,
      is_active: accountForm.is_active,
      display_order:
        accountForm.display_order === '' || accountForm.display_order === null
          ? undefined
          : Number(accountForm.display_order),
    };

    if (!payload.bank_name || !payload.account_no || !payload.account_name || !payload.acq_id) {
      toast.error('Vui lòng nhập đầy đủ thông tin bắt buộc');
      return;
    }

    try {
      setSavingAccount(true);
      if (editingAccountId) {
        await bankAccountAPI.updateBankAccount(editingAccountId, payload);
        toast.success('Đã cập nhật tài khoản ngân hàng');
      } else {
        await bankAccountAPI.createBankAccount(payload);
        toast.success('Đã tạo tài khoản ngân hàng');
      }
      await fetchAccounts();
      closeAccountModal();
    } catch (error) {
      console.error('Cannot save bank account', error);
    } finally {
      setSavingAccount(false);
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccountId(account.id);
    setAccountForm({
      bank_name: account.bank_name || '',
      short_name: account.short_name || '',
      account_no: account.account_no || '',
      account_name: account.account_name || '',
      acq_id: account.acq_id || '',
      vietqr_client_id: account.vietqr_client_id || '',
      vietqr_api_key: '',
      static_qr_url: account.static_qr_url || '',
      notes: account.notes || '',
      display_order: account.display_order ?? '',
      is_active: account.is_active ?? true,
    });
    if (account.bank_name || account.acq_id) {
      const matched = findBankByInput(vietqrBanks, account.acq_id || account.bank_name);
      setBankSearch(
        matched
          ? formatBankOption(matched)
          : `${account.bank_name || ''}${account.acq_id ? ` (${account.acq_id})` : ''}`,
      );
    } else {
      setBankSearch('');
    }
    setShowAccountModal(true);
  };

  const handleDeleteAccount = async (account) => {
    if (!window.confirm(`Xóa tài khoản "${account.bank_name} - ${account.account_no}"?`)) {
      return;
    }
    try {
      await bankAccountAPI.deleteBankAccount(account.id);
      toast.success('Đã xóa tài khoản ngân hàng');
      await fetchAccounts();
      if (editingAccountId === account.id) {
        closeAccountModal();
      }
    } catch (error) {
      console.error('Cannot delete bank account', error);
    }
  };

  const handleToggleAccountActive = async (account) => {
    try {
      await bankAccountAPI.updateBankAccount(account.id, { is_active: !account.is_active });
      toast.success('Đã cập nhật trạng thái tài khoản');
      await fetchAccounts();
    } catch (error) {
      console.error('Cannot toggle bank account', error);
    }
  };

  const handleAssignSubmit = async (event) => {
    event.preventDefault();
    if (!branchId) {
      toast.error('Vui lòng chọn chi nhánh trước khi gán tài khoản');
      return;
    }
    if (!assignForm.bank_account_id) {
      toast.error('Vui lòng chọn tài khoản cần gán');
      return;
    }
    const payload = {
      bank_account_id: assignForm.bank_account_id,
      is_active: assignForm.is_active,
      display_order:
        assignForm.display_order === '' || assignForm.display_order === null
          ? undefined
          : Number(assignForm.display_order),
    };
    try {
      setAssigning(true);
      await bankAccountAPI.assignBankAccountToBranch(branchId, payload);
      toast.success('Đã gán tài khoản cho chi nhánh');
      setAssignForm({ ...emptyAssignForm });
      await fetchAssignments();
    } catch (error) {
      console.error('Cannot assign bank account to branch', error);
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveAssignment = async (assignment) => {
    if (!branchId) return;
    if (!window.confirm(`Gỡ tài khoản ${assignment.bank_account?.bank_name || ''} khỏi chi nhánh?`)) {
      return;
    }
    try {
      await bankAccountAPI.removeBankAccountFromBranch(branchId, assignment.id);
      toast.success('Đã gỡ tài khoản khỏi chi nhánh');
      await fetchAssignments();
    } catch (error) {
      console.error('Cannot remove assignment', error);
    }
  };

  const handleUpdateAssignment = async (assignmentId, data) => {
    if (!branchId) return;
    try {
      await bankAccountAPI.updateBranchBankAccount(branchId, assignmentId, data);
      await fetchAssignments();
    } catch (error) {
      console.error('Cannot update assignment', error);
    }
  };

  // (#002727) Thứ tự hiển thị trên màn = thứ tự bộ sinh QR dùng để chọn tài khoản nhận tiền.
  // Luôn đánh lại số 1..N liên tục sau mỗi lần đổi, để "vị trí 1" trên màn đúng nghĩa đen là
  // display_order = 1 (dữ liệu thật của QBT đang là 331 và 3 — số rời rạc rất khó hiểu).
  const persistOrder = useCallback(
    async (orderedIds) => {
      if (!branchId) return false;
      const current = new Map(branchAssignments.map((item) => [item.id, item.display_order]));
      const changed = orderedIds
        .map((id, index) => ({ id, display_order: index + 1 }))
        .filter((item) => current.get(item.id) !== item.display_order);
      if (!changed.length) return true;
      try {
        // Tuần tự, không Promise.all: tránh nhiều lệnh ghi cùng lúc lên cùng 1 chi nhánh.
        for (const item of changed) {
          await bankAccountAPI.updateBranchBankAccount(branchId, item.id, {
            display_order: item.display_order,
          });
        }
        return true;
      } catch (error) {
        console.error('Cannot update display order', error);
        toast.error('Không lưu được thứ tự tài khoản');
        return false;
      }
    },
    [branchId, branchAssignments],
  );

  // Sắp xếp lại theo con số người dùng vừa gõ vào ô "Thứ tự" của MỘT dòng.
  const handleSaveOrder = async (assignment) => {
    const draft = orderDrafts[assignment.id];
    if (draft === undefined) return;
    const trimmed = String(draft).trim();
    if (trimmed === '') {
      setOrderDrafts((prev) => {
        const next = { ...prev };
        delete next[assignment.id];
        return next;
      });
      return;
    }
    const wanted = Number(trimmed);
    if (!Number.isFinite(wanted) || wanted < 1) {
      toast.error('Thứ tự phải là số nguyên từ 1 trở lên');
      return;
    }
    const others = branchAssignments.filter((item) => item.id !== assignment.id);
    // Chèn dòng này vào đúng vị trí người dùng muốn (1 = đầu danh sách), phần còn lại giữ nguyên
    // thứ tự tương đối rồi được đánh số lại liên tục.
    const targetIndex = Math.min(Math.max(Math.round(wanted) - 1, 0), others.length);
    const orderedIds = [
      ...others.slice(0, targetIndex).map((item) => item.id),
      assignment.id,
      ...others.slice(targetIndex).map((item) => item.id),
    ];
    setSavingOrderId(assignment.id);
    try {
      if (await persistOrder(orderedIds)) {
        toast.success(`Đã lưu thứ tự: ${assignment.bank_account?.bank_name || 'Tài khoản'} ở vị trí ${targetIndex + 1}`);
        await fetchAssignments();
      }
    } finally {
      setSavingOrderId(null);
    }
  };

  // Nút ▲/▼ — cách đổi thứ tự dễ bấm trên điện thoại, không phải gõ số.
  const handleMoveAssignment = async (assignment, direction) => {
    const index = branchAssignments.findIndex((item) => item.id === assignment.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= branchAssignments.length) return;
    const reordered = [...branchAssignments];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setSavingOrderId(assignment.id);
    try {
      if (await persistOrder(reordered.map((item) => item.id))) {
        toast.success(`Đã chuyển ${assignment.bank_account?.bank_name || 'tài khoản'} tới vị trí ${target + 1}`);
        await fetchAssignments();
      }
    } finally {
      setSavingOrderId(null);
    }
  };

  // Kéo–thả: PointerSensor cần đi được 6px mới coi là kéo (đỡ nhầm với cú bấm nút),
  // TouchSensor giữ 180ms mới bắt đầu kéo để ngón tay vẫn vuốt cuộn trang được như thường.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = branchAssignments.findIndex((item) => item.id === active.id);
    const newIndex = branchAssignments.findIndex((item) => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(branchAssignments, oldIndex, newIndex);
    const moved = branchAssignments[oldIndex];
    // Đổi chỗ ngay trên màn để tay thả tới đâu thấy tới đó, rồi mới ghi xuống máy chủ.
    setBranchAssignments(reordered.map((item, idx) => ({ ...item, display_order: idx + 1 })));
    setSavingOrderId(moved.id);
    try {
      if (await persistOrder(reordered.map((item) => item.id))) {
        toast.success(
          `Đã chuyển ${moved.bank_account?.bank_name || 'tài khoản'} tới vị trí ${newIndex + 1}`,
        );
      }
      await fetchAssignments();
    } finally {
      setSavingOrderId(null);
    }
  };

  const availableAccountsForAssign = useMemo(() => {
    const assignedIds = new Set(branchAssignments.map((item) => item.bank_account_id));
    return accounts.filter((account) => !assignedIds.has(account.id));
  }, [accounts, branchAssignments]);

  const handleBankInputChange = (value) => {
    setBankSearch(value);
    const matched = findBankByInput(vietqrBanks, value);
    if (matched) {
      setAccountForm((prev) => ({
        ...prev,
        bank_name: matched.name,
        acq_id: matched.bin,
        short_name: matched.shortName || matched.short_name || '',
      }));
    }
  };

  const handleSavePaymentQrConfig = async () => {
    let valueToSave = paymentQrConfigEditValue.trim();
    if (!valueToSave && paymentQrConfig) {
      toast.error('Giá trị config không được để trống');
      return;
    }
    if (valueToSave) {
      try {
        JSON.parse(valueToSave);
      } catch {
        toast.error('Giá trị phải là JSON hợp lệ');
        return;
      }
    }
    try {
      setSavingPaymentQrConfig(true);
      if (paymentQrConfig?.id) {
        await configurationAPI.updateConfiguration(paymentQrConfig.id, { value: valueToSave || '{}' });
        toast.success('Đã cập nhật cấu hình PAYMENT_QR_ACCOUNT_CONFIG');
      } else {
        await configurationAPI.createConfiguration({
          key: CONFIG_KEY_PAYMENT_QR_ACCOUNT,
          value: valueToSave || '{}',
          type: 'JSON',
          category: 'PAYMENT',
          description: 'Cấu hình tài khoản QR thanh toán',
          branch_id: branchId || null,
          is_active: true,
        });
        toast.success('Đã tạo cấu hình PAYMENT_QR_ACCOUNT_CONFIG');
      }
      await fetchPaymentQrConfig();
    } catch (error) {
      console.error('Cannot save PAYMENT_QR_ACCOUNT_CONFIG', error);
      toast.error(error?.response?.data?.message || 'Lưu cấu hình thất bại');
    } finally {
      setSavingPaymentQrConfig(false);
    }
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Quản lý tài khoản ngân hàng</h1>
            <p>Thiết lập tài khoản nhận tiền toàn hệ thống và gán cho từng chi nhánh</p>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h5 className="mb-0">Danh sách tài khoản ngân hàng</h5>
              <small className="text-muted">
                Số tiền nhận: đơn đã thanh toán (PAID), theo paid_at — cùng logic báo cáo doanh thu
              </small>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="text-muted small">
                {loadingAccounts ? 'Đang tải...' : `${accounts.length} tài khoản`}
              </span>
              <Button
                variant="primary"
                onClick={openAddAccountModal}
              >
                Thêm tài khoản mới
              </Button>
            </div>
          </div>
          <div className="card-body">
            <div className="border rounded p-3 mb-3 bg-light">
              <DateRangePicker
                fromDate={revenueDateFrom}
                toDate={revenueDateTo}
                onChange={(from, to) => {
                  setRevenueDateFrom(from);
                  setRevenueDateTo(to);
                }}
                defaultPreset="last7days"
              />
              <small className="text-muted d-block mt-1">
                Khoảng thời gian: {revenueDateFrom} → {revenueDateTo}
              </small>
            </div>
            <div className="table-responsive">
              {accounts.length === 0 && !loadingAccounts ? (
                <div className="text-center text-muted py-5">
                  Chưa có tài khoản nào. Bấm &quot;Thêm tài khoản mới&quot;.
                </div>
              ) : (
                <table className="table align-middle table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Ngân hàng</th>
                      <th>Số TK</th>
                      <th>Chủ TK</th>
                      <th>Mã QR tĩnh</th>
                      <th className="text-end">Số tiền nhận</th>
                      <th className="text-end">Số đơn</th>
                      <th>Trạng thái</th>
                      <th className="text-end">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.id}>
                        <td>
                          <div className="fw-semibold">{account.short_name || account.bank_name}</div>
                          <small className="text-muted">{account.acq_id}</small>
                        </td>
                        <td>{account.account_no}</td>
                        <td>{account.account_name}</td>
                        <td>
                          {account.static_qr_url ? (
                            <img
                              src={account.static_qr_url}
                              alt="Mã QR tĩnh"
                              style={{ width: 56, height: 56, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 4, background: '#fff' }}
                              data-testid={`static-qr-thumb-${account.id}`}
                            />
                          ) : (
                            <span className="text-muted small">Chưa có</span>
                          )}
                        </td>
                        <td className="text-end text-nowrap fw-semibold">{formatVnd(account.received_amount)}</td>
                        <td className="text-end">{account.received_order_count ?? '—'}</td>
                        <td>
                          <span className={`badge ${account.is_active ? 'bg-success' : 'bg-secondary'}`}>
                            {account.is_active ? 'Hoạt động' : 'Tạm tắt'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex justify-content-end flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditAccount(account)}
                            >
                              Sửa
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handleToggleAccountActive(account)}
                            >
                              {account.is_active ? 'Tắt' : 'Bật'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteAccount(account)}
                            >
                              Xóa
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <Modal
          show={showAccountModal}
          onHide={closeAccountModal}
          size="lg"
          scrollable
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{editingAccountId ? 'Cập nhật tài khoản' : 'Thêm tài khoản mới'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form
              id="bank-account-form"
              onSubmit={handleAccountSubmit}
            >
              <div className="mb-3">
                <label className="form-label">Ngân hàng (VietQR) *</label>
                <input
                  list="vietqr-bank-list-modal"
                  className="form-control"
                  placeholder="Nhập tên, mã hoặc BIN ngân hàng"
                  value={bankSearch}
                  onChange={(e) => handleBankInputChange(e.target.value)}
                  disabled={loadingBanks}
                  required
                />
                <datalist id="vietqr-bank-list-modal">
                  {vietqrBanks.map((bank) => (
                    <option
                      key={bank.bin}
                      value={formatBankOption(bank)}
                    />
                  ))}
                </datalist>
                <div className="form-text">
                  {loadingBanks
                    ? 'Đang tải danh sách ngân hàng VietQR...'
                    : 'Chọn ngân hàng để tự động điền tên và mã BIN.'}
                </div>
              </div>

              <Row>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">Mã ngân hàng (BIN)</label>
                  <input
                    className="form-control"
                    value={accountForm.acq_id}
                    readOnly
                    placeholder="Chọn ngân hàng để điền tự động"
                  />
                </Col>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">Tên ngân hàng</label>
                  <input
                    className="form-control"
                    value={accountForm.bank_name}
                    readOnly
                    placeholder="Chọn ngân hàng để điền tự động"
                  />
                </Col>
              </Row>

              <Row>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">Tên viết tắt</label>
                  <input
                    className="form-control"
                    value={accountForm.short_name}
                    onChange={(e) => setAccountForm({ ...accountForm, short_name: e.target.value })}
                    placeholder="Tự điền từ VietQR hoặc nhập tay (VD: VCB, BIDV)"
                  />
                  <div className="form-text">Từ api.vietqr.io/v2/banks khi chọn ngân hàng, có thể sửa.</div>
                </Col>
              </Row>

              <Row>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">Số tài khoản *</label>
                  <input
                    className="form-control"
                    value={accountForm.account_no}
                    onChange={(e) => setAccountForm({ ...accountForm, account_no: e.target.value })}
                    required
                  />
                </Col>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">Chủ tài khoản *</label>
                  <input
                    className="form-control"
                    value={accountForm.account_name}
                    onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })}
                    required
                  />
                </Col>
              </Row>

              <Row>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">VietQR Client ID</label>
                  <input
                    className="form-control"
                    value={accountForm.vietqr_client_id}
                    onChange={(e) => setAccountForm({ ...accountForm, vietqr_client_id: e.target.value })}
                  />
                </Col>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">VietQR API Key</label>
                  <input
                    className="form-control"
                    value={accountForm.vietqr_api_key}
                    onChange={(e) => setAccountForm({ ...accountForm, vietqr_api_key: e.target.value })}
                    placeholder={editingAccountId ? 'Nhập để thay đổi' : ''}
                  />
                </Col>
              </Row>

              <Row>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">Thứ tự hiển thị</label>
                  <input
                    type="number"
                    className="form-control"
                    value={accountForm.display_order}
                    onChange={(e) => setAccountForm({ ...accountForm, display_order: e.target.value })}
                  />
                </Col>
                <Col
                  md={6}
                  className="mb-3"
                >
                  <label className="form-label">Trạng thái</label>
                  <select
                    className="form-select"
                    value={accountForm.is_active ? 'true' : 'false'}
                    onChange={(e) => setAccountForm({ ...accountForm, is_active: e.target.value === 'true' })}
                  >
                    <option value="true">Đang hoạt động</option>
                    <option value="false">Tạm tắt</option>
                  </select>
                </Col>
              </Row>

              {/* (#195715) Mã QR TĨNH của tài khoản — admin tải lên, KHÔNG kèm số tiền.
                  Dùng làm mã thay thế khi sinh mã QR số tiền động lỗi. */}
              <div className="mb-3">
                <label className="form-label">Mã QR tĩnh của tài khoản</label>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={handleStaticQrFileChange}
                    disabled={uploadingStaticQr || savingAccount}
                    data-testid="static-qr-file-input"
                  />
                </div>
                <div className="form-text">
                  Ảnh QR của chính tài khoản này (khách tự nhập số tiền, KHÔNG kèm sẵn số tiền).
                  Tối đa 5MB, chỉ nhận ảnh PNG/JPG/WebP. Ảnh lưu ở kho ảnh dùng chung của hệ thống,
                  chỉ quản trị viên (ADMIN) tải lên được.
                </div>
                {uploadingStaticQr && (
                  <div className="text-muted small mt-1">Đang tải ảnh lên...</div>
                )}
                {accountForm.static_qr_url ? (
                  <div className="d-flex align-items-center gap-3 mt-2">
                    <img
                      src={accountForm.static_qr_url}
                      alt="Mã QR tĩnh của tài khoản"
                      style={{ width: 120, height: 120, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 6, background: '#fff' }}
                      data-testid="static-qr-preview"
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setAccountForm((prev) => ({ ...prev, static_qr_url: '' }))}
                      disabled={uploadingStaticQr}
                    >
                      Bỏ ảnh
                    </button>
                  </div>
                ) : (
                  <div className="text-muted small mt-2">Chưa có ảnh mã QR tĩnh.</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Ghi chú</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={accountForm.notes}
                  onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })}
                />
              </div>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeAccountModal}
            >
              Đóng
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="bank-account-form"
              disabled={savingAccount}
            >
              {savingAccount ? 'Đang lưu...' : editingAccountId ? 'Cập nhật tài khoản' : 'Thêm tài khoản'}
            </Button>
          </Modal.Footer>
        </Modal>

        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="mb-0">Gán tài khoản cho chi nhánh</h5>
              <small className="text-muted">Mỗi chi nhánh chỉ có 1 tài khoản mặc định</small>
            </div>
          </div>
          <div className="card-body">
            {!branchId ? (
              <div className="alert alert-warning mb-0">Vui lòng chọn chi nhánh để cấu hình.</div>
            ) : (
              <>
                <form
                  className="row g-3 mb-4"
                  onSubmit={handleAssignSubmit}
                >
                  <div className="col-md-4">
                    <label className="form-label">Tài khoản trong hệ thống *</label>
                    <select
                      className="form-select"
                      value={assignForm.bank_account_id}
                      onChange={(e) => setAssignForm({ ...assignForm, bank_account_id: e.target.value })}
                    >
                      <option value="">-- Chọn tài khoản --</option>
                      {availableAccountsForAssign.map((account) => (
                        <option
                          key={account.id}
                          value={account.id}
                        >
                          {account.short_name || account.bank_name} - ({account.account_name}) - {account.account_no}
                        </option>
                      ))}
                      {availableAccountsForAssign.length === 0 && (
                        <option
                          value=""
                          disabled
                        >
                          Chi nhánh đã gán hết tài khoản hiện có
                        </option>
                      )}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Thứ tự</label>
                    <input
                      type="number"
                      className="form-control"
                      value={assignForm.display_order}
                      onChange={(e) => setAssignForm({ ...assignForm, display_order: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Trạng thái</label>
                    <select
                      className="form-select"
                      value={assignForm.is_active ? 'true' : 'false'}
                      onChange={(e) => setAssignForm({ ...assignForm, is_active: e.target.value === 'true' })}
                    >
                      <option value="true">Hoạt động</option>
                      <option value="false">Tạm tắt</option>
                    </select>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button
                      type="submit"
                      className="btn btn-success w-100"
                      disabled={assigning}
                    >
                      {assigning ? 'Đang gán...' : 'Gán tài khoản'}
                    </button>
                  </div>
                </form>

                {loadingAssignments ? (
                  <div className="text-center text-muted py-4">Đang tải danh sách...</div>
                ) : branchAssignments.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    Chi nhánh chưa có tài khoản nào. Hãy gán ít nhất một tài khoản active.
                  </div>
                ) : (
                  <>
                    {/* (#002727) Chú thích SAI ("vị trí 1 nhận toàn bộ") chính là gốc của vụ hiểu
                        nhầm 03/09. Nay mô tả lấy thẳng từ máy chủ (description_lines), dựng bằng
                        đúng hàm mà bộ sinh QR dùng, nên không thể lệch hành vi thật. */}
                    {/* (#214922, sếp chốt 21:49 06/09/2026 qua Kênh duyệt — phương án "giữ nguyên,
                        chỉ hiện cảnh báo") Lớp doanh thu vẫn chạy y như cũ; điều DUY NHẤT đổi là màn
                        này nói thẳng khi nó đang vô hiệu hoá mốc tiền. Trước đây hạn mức doanh thu
                        chỉ hiện dạng chỉ-đọc ở cuối trang nên nhìn màn hình không thể biết tiền
                        đang KHÔNG đi theo mốc đã đặt (#212048: 27/43 đơn ngày 06/09 vào sai chỗ). */}
                    {splitConfig?.revenue_override?.active && (
                      <div className="alert alert-danger py-2 small mb-2" data-testid="revenue-override-warning">
                        <div className="fw-bold mb-1">
                          ⚠️ Tiền đang KHÔNG đi theo mốc tiền đã đặt
                        </div>
                        <ul className="mb-0 ps-3">
                          {splitConfig.revenue_override.warning_lines.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="alert alert-secondary py-2 small mb-2">
                      <div className="fw-semibold mb-1">Tiền chuyển khoản đang chảy vào đâu</div>
                      {loadingSplitConfig ? (
                        <div className="text-muted">Đang đọc quy tắc chia tiền...</div>
                      ) : (
                        <ul className="mb-1 ps-3">
                          {(splitConfig?.description_lines || [
                            'Vị trí 1 nhận toàn bộ tiền chuyển khoản (không chia theo mức)',
                          ]).map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                          {splitConfig?.revenue?.active && (
                            <li>
                              Ngoài ra còn lớp <strong>tự xoay theo doanh thu</strong> tuần/tháng: vị trí đang
                              chọn mà vượt hạn mức thì tụt xuống vị trí kế tiếp (xem chi tiết ở mục
                              &quot;Quy tắc chia tiền&quot; bên dưới).
                            </li>
                          )}
                        </ul>
                      )}
                      Vị trí 1 bị tắt thì vị trí kế tiếp lên thay. Đổi vị trí bằng cách{' '}
                      <strong>kéo–thả</strong> biểu tượng <GripVertical /> , bấm ▲▼, hoặc gõ thẳng số vào ô
                      &quot;Thứ tự&quot;.
                    </div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext
                        items={branchAssignments.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div>
                          {branchAssignments.map((assignment, index) => (
                            <SortableAssignmentCard
                              key={assignment.id}
                              assignment={assignment}
                              index={index}
                              total={branchAssignments.length}
                              savingOrderId={savingOrderId}
                              orderDrafts={orderDrafts}
                              setOrderDrafts={setOrderDrafts}
                              onSaveOrder={handleSaveOrder}
                              onMove={handleMoveAssignment}
                              onUpdate={handleUpdateAssignment}
                              onRemove={handleRemoveAssignment}
                              positionNote={splitConfig?.position_notes?.[index]}
                              revenuePosition={splitConfig?.revenue_override?.positions?.[index]}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* (#002727, lệnh sếp 06/09/2026) Bật/tắt và sửa mốc tiền NGAY TẠI ĐÂY — trước đây muốn đổi
            phải gõ tay vào ô JSON bên dưới, nên cấu hình dễ nằm chết mà không ai biết. */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">Quy tắc chia tiền theo mức</h5>
          </div>
          <div className="card-body">
            {!branchId ? (
              <div className="alert alert-info mb-0">Chọn chi nhánh để xem quy tắc chia tiền.</div>
            ) : loadingSplitConfig ? (
              <div className="text-muted py-2">Đang tải quy tắc...</div>
            ) : (
              <>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="split-enabled"
                    role="switch"
                    checked={splitEnabledDraft}
                    onChange={(e) => setSplitEnabledDraft(e.target.checked)}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="split-enabled">
                    Chia tiền theo mức đơn
                  </label>
                  <div className="small text-muted">
                    Tắt = tài khoản ở vị trí 1 nhận 100% tiền chuyển khoản.
                  </div>
                </div>

                {splitEnabledDraft && (
                  <div className="mb-3">
                    <div className="fw-semibold mb-1">Mốc tiền</div>
                    <div className="small text-muted mb-2">
                      Đơn dưới mốc 1 vào <strong>vị trí 1</strong>; từ mốc 1 sang <strong>vị trí 2</strong>;
                      từ mốc 2 sang <strong>vị trí 3</strong>...
                    </div>
                    {tierDrafts.map((tier, idx) => (
                      // Nhãn nằm TRÊN một dòng riêng, ô nhập ở dòng dưới. Nhồi cả nhãn + ô + nút
                      // vào một hàng ngang thì trên khổ 390px ô bị bóp còn ~60px và số "300000"
                      // hiện thành "300(" — đúng kiểu lỗi ô Thứ tự hồi đầu việc này.
                      <div key={`tier-${idx}`} className="border rounded p-2 mb-2">
                        <div className="small mb-1">
                          <span className="text-muted">Từ mốc {idx + 1} → </span>
                          <span className="fw-semibold">vị trí {idx + 2}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            className="form-control"
                            style={{ minWidth: 120, maxWidth: 200, fontSize: 16 }}
                            aria-label={`Mốc tiền thứ ${idx + 1}`}
                            value={tier}
                            onChange={(e) => handleChangeTier(idx, e.target.value)}
                            placeholder="300000"
                          />
                          <span className="small text-muted">đ</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-auto"
                            onClick={() => handleRemoveTier(idx)}
                          >
                            Xoá
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleAddTier}
                      disabled={tierDrafts.length >= 3}
                    >
                      + Thêm mốc
                    </button>
                    {splitConfig?.active_bank_account_count != null &&
                      tierDrafts.length > Math.max(splitConfig.active_bank_account_count - 1, 0) && (
                        <div className="alert alert-warning py-2 small mt-2 mb-0">
                          Chi nhánh chỉ có {splitConfig.active_bank_account_count} tài khoản đang bật, nên
                          các mốc vượt quá sẽ dồn hết vào vị trí {splitConfig.active_bank_account_count}.
                        </div>
                      )}
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingSplitConfig}
                  onClick={handleSaveSplitConfig}
                >
                  {savingSplitConfig ? 'Đang lưu...' : 'Lưu quy tắc'}
                </button>

                <hr />

                {/* Hai lớp dưới KHÔNG bị bỏ (NĐH chốt giữ nguyên) — nhưng phải hiện rõ chi nhánh
                    đang bật lớp nào, vì chính lớp doanh thu đẩy đơn nhỏ của QBT xuống vị trí 2. */}
                <div className="fw-semibold mb-2">Các lớp khác đang áp dụng</div>
                <div className="mb-2">
                  <span className="me-2">Theo khung giờ:</span>
                  {splitConfig?.time_ranges?.length ? (
                    <span className="badge bg-warning text-dark">
                      ĐANG BẬT — {splitConfig.time_ranges.map((r) => `${r.start}–${r.end}`).join(', ')} → đẩy
                      xuống ít nhất vị trí 2
                    </span>
                  ) : (
                    <span className="badge bg-light text-muted border">Tắt</span>
                  )}
                </div>
                <div className="mb-2">
                  <span className="me-2">Tự xoay theo doanh thu:</span>
                  {splitConfig?.revenue?.active ? (
                    <span className="badge bg-warning text-dark">ĐANG BẬT</span>
                  ) : (
                    <span className="badge bg-light text-muted border">Tắt</span>
                  )}
                  {splitConfig?.revenue_override?.active && (
                    <span className="badge bg-danger ms-2">ĐANG ĐÈ MỐC TIỀN</span>
                  )}
                </div>
                {splitConfig?.revenue?.active && (
                  <div className="table-responsive">
                    {/* (#214922) Bảng cũ chỉ có hạn mức — nhìn con số trần trụi không biết đã dùng
                        tới đâu. Nay kèm doanh thu THẬT của kỳ hiện tại (máy chủ đo bằng đúng truy
                        vấn mà bộ sinh QR dùng) và nói rõ đơn đang rơi vào vị trí nào. */}
                    <table className="table table-sm table-bordered mb-2" style={{ maxWidth: 760 }}>
                      <thead>
                        <tr>
                          <th>Vị trí</th>
                          <th>Tuần: đã nhận / hạn mức</th>
                          <th>Tháng: đã nhận / hạn mức</th>
                          <th>Đơn thực tế vào</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(splitConfig.revenue.limits_week || []).map((w, idx) => {
                          const pos = splitConfig?.revenue_override?.positions?.[idx];
                          const mLim = Number(splitConfig.revenue.limits_month?.[idx] || 0);
                          const money = (n) => Number(n || 0).toLocaleString('vi-VN') + 'đ';
                          return (
                            <tr key={`rev-${idx}`} className={pos?.exceeded ? 'table-danger' : undefined}>
                              <td>
                                {idx + 1}
                                {/* Cấu hình có 4 hạn mức nhưng chi nhánh thường ít tài khoản hơn —
                                    nói rõ dòng thừa để không ai tưởng đang thiếu số liệu. */}
                                <div className="small text-muted">
                                  {pos?.account_label || 'chưa có tài khoản ở vị trí này'}
                                </div>
                              </td>
                              <td>
                                {pos ? `${money(pos.week_revenue)} / ` : ''}
                                {money(w)}
                              </td>
                              <td>
                                {pos ? `${money(pos.month_revenue)} / ` : ''}
                                {money(mLim)}
                              </td>
                              <td>
                                {!pos ? (
                                  '—'
                                ) : pos.actual_position === pos.position ? (
                                  <span className="text-success">Đúng vị trí {pos.position}</span>
                                ) : (
                                  <span className="text-danger fw-semibold">
                                    Đã tràn → vị trí {pos.actual_position}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="small text-muted">
                      Vị trí đang chọn mà vượt hạn mức tuần hoặc tháng thì tiền tự tụt xuống vị trí kế tiếp.
                      Doanh thu tuần tính từ Thứ Hai 00:00, doanh thu tháng tính từ ngày 1 (giờ Việt Nam),
                      chỉ đếm đơn đã thanh toán.
                    </div>
                  </div>
                )}
                <div className="small text-muted">
                  Muốn đổi giá trị hai lớp này thì sửa ở ô JSON &quot;Nâng cao&quot; bên dưới hoặc màn Cấu
                  hình hệ thống.
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="mb-0">Cấu hình QR thanh toán (nâng cao — JSON)</h5>
              <div className="small text-muted">
                Chỉ dùng khi cần sửa khung giờ, hạn mức doanh thu hoặc nội dung chuyển khoản.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowRawQrConfig((v) => !v)}
            >
              {showRawQrConfig ? 'Ẩn' : 'Hiện'}
            </button>
          </div>
          <div className="card-body" style={{ display: showRawQrConfig ? undefined : 'none' }}>
            {!branchId && (
              <div className="alert alert-info mb-3">
                Chọn chi nhánh để xem/cấu hình config theo chi nhánh. Để trống = config global.
              </div>
            )}
            {loadingPaymentQrConfig ? (
              <div className="text-muted py-3">Đang tải cấu hình...</div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Key</label>
                  <input
                    type="text"
                    className="form-control font-monospace"
                    value={CONFIG_KEY_PAYMENT_QR_ACCOUNT}
                    readOnly
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Giá trị (JSON)</label>
                  <textarea
                    className="form-control font-monospace"
                    style={{ minHeight: '200px', height: 'auto', resize: 'vertical' }}
                    rows={Math.max(10, (paymentQrConfigEditValue || '').split('\n').length + 2)}
                    placeholder='{"branch_id": "..."}'
                    value={paymentQrConfigEditValue}
                    onChange={(e) => setPaymentQrConfigEditValue(e.target.value)}
                    spellCheck={false}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingPaymentQrConfig}
                  onClick={handleSavePaymentQrConfig}
                >
                  {savingPaymentQrConfig ? 'Đang lưu...' : 'Lưu cấu hình'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default BankAccountManagementPage;
